import { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import DynamicFields from '../../components/DynamicFields';
import { CallsApi, CrmApi } from '../../api/resources';
import { TYPE_LABEL, fmtDuration } from './callUtils';
import { errorMessage, validationErrors } from '../../api/client';
import { fmtDate, fromLocalInput } from './useCrmLookups';

const TYPES = ['call', 'whatsapp', 'email', 'meeting', 'visit', 'note'];

/** Log a follow-up and update the lead's status in one step. */
export default function FollowupFormModal({ lead, lookups, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: 'call',
    status_id: lead.status_id,
    remarks: '',
    next_follow_up_at: '',
    call_log_id: '',
    custom_values: {},
  });
  const [calls, setCalls] = useState([]);

  // The staff member's own recent calls to this lead's number, to prove the call happened.
  useEffect(() => {
    CallsApi.linkableCalls(lead.id)
      .then((list) => {
        setCalls(list);
        if (list.length) setForm((f) => ({ ...f, call_log_id: String(list[0].id) }));
      })
      .catch(() => {});
  }, [lead.id]);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setBanner('');
    try {
      await CrmApi.followups.create(lead.id, {
        ...form,
        call_log_id: form.type === 'call' && form.call_log_id ? Number(form.call_log_id) : null,
        next_follow_up_at: fromLocalInput(form.next_follow_up_at),
      });
      onSaved();
    } catch (err) {
      setErrors(validationErrors(err));
      setBanner(errorMessage(err, 'Could not save follow-up.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Follow-up · ${lead.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save follow-up'}
          </button>
        </>
      }
    >
      {banner && <div className="notice notice-error">{banner}</div>}
      <form onSubmit={submit}>
        <div className="field">
          <label>Type *</label>
          <select value={form.type} onChange={set('type')}>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t[0].toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
          {errors.type && <div className="err">{errors.type}</div>}
        </div>
        {form.type === 'call' && (
          <div className="field">
            <label>Phone call</label>
            <select value={form.call_log_id} onChange={set('call_log_id')}>
              <option value="">{calls.length ? 'Not linked to a recorded call' : 'No recent call to this number found'}</option>
              {calls.map((c) => (
                <option key={c.id} value={c.id}>
                  {TYPE_LABEL[c.call_type]} · {fmtDate(c.call_timestamp)} · {fmtDuration(c.call_duration)}
                  {c.recordings.length ? ' · recorded' : ''}
                </option>
              ))}
            </select>
            <div className="sub">Linking the call from the Android app shows this follow-up was backed by a real call.</div>
            {errors.call_log_id && <div className="err">{errors.call_log_id}</div>}
          </div>
        )}
        <div className="field">
          <label>Status *</label>
          <select value={form.status_id} onChange={set('status_id')}>
            {lookups.statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {errors.status_id && <div className="err">{errors.status_id}</div>}
        </div>
        <div className="field">
          <label>Remarks</label>
          <textarea rows={3} value={form.remarks} onChange={set('remarks')} />
        </div>
        <div className="field">
          <label>Next follow-up</label>
          <input
            type="datetime-local"
            value={form.next_follow_up_at}
            onChange={set('next_follow_up_at')}
          />
          {errors.next_follow_up_at && <div className="err">{errors.next_follow_up_at}</div>}
        </div>

        <DynamicFields
          fields={lookups.followupFields}
          values={form.custom_values}
          errors={errors}
          onChange={(custom_values) => setForm((f) => ({ ...f, custom_values }))}
        />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
